import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiX, FiDroplet } from 'react-icons/fi';
import { getFormErrorMessage } from '../../utils/helpers';
import LoadingSpinner from '../common/LoadingSpinner';
import { Modal } from '../../design-system';

const EstablishmentForm = ({ establishment, onSave, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!establishment;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: establishment?.name || '',
      description: establishment?.description || '',
      address: establishment?.address || '',
      city: establishment?.city || '',
      country: establishment?.country || '',
      postal_code: establishment?.postal_code || '',
      phone: establishment?.phone || '',
      email: establishment?.email || '',
      website: establishment?.website || '',
      is_active: establishment?.is_active ?? true,
      show_schedule_colors: establishment?.show_schedule_colors ?? false,
    },
  });

  const showScheduleColors = watch('show_schedule_colors');

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      const establishmentData = {
        ...data,
        email: data.email || null,
        phone: data.phone || null,
        website: data.website || null,
        address: data.address || null,
        city: data.city || null,
        country: data.country || null,
        postal_code: data.postal_code || null,
        description: data.description || null,
        show_schedule_colors: data.show_schedule_colors ?? false,
      };

      await onSave(establishmentData);
    } catch (error) {
      console.error('Error saving establishment:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size="2xl"
      showCloseButton={false}
    >
      <Modal.Header>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            {isEditing ? 'Editar Establecimiento' : 'Nuevo Establecimiento'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>
      </Modal.Header>

      <Modal.Body className="max-h-[60vh] overflow-y-auto">
        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" id="establishment-form">
              {/* Name */}
              <div>
                <label htmlFor="name" className="form-label">
                  Nombre del establecimiento *
                </label>
                <input
                  id="name"
                  type="text"
                  className={`form-input ${errors.name ? 'border-danger-500' : ''}`}
                  placeholder="Hotel Example, Hostal Paradise, etc."
                  {...register('name', {
                    required: true,
                    minLength: 2,
                  })}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-danger-600">
                    {getFormErrorMessage(errors, 'name')}
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="form-label">
                  Descripción
                </label>
                <textarea
                  id="description"
                  rows={3}
                  className="form-input"
                  placeholder="Descripción del establecimiento..."
                  {...register('description')}
                />
              </div>

              {/* Address Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="address" className="form-label">
                    Dirección
                  </label>
                  <input
                    id="address"
                    type="text"
                    className="form-input"
                    placeholder="Calle, número, etc."
                    {...register('address')}
                  />
                </div>

                <div>
                  <label htmlFor="city" className="form-label">
                    Ciudad
                  </label>
                  <input
                    id="city"
                    type="text"
                    className="form-input"
                    placeholder="Madrid, Barcelona, etc."
                    {...register('city')}
                  />
                </div>

                <div>
                  <label htmlFor="country" className="form-label">
                    País
                  </label>
                  <input
                    id="country"
                    type="text"
                    className="form-input"
                    placeholder="España, México, etc."
                    {...register('country')}
                  />
                </div>

                <div>
                  <label htmlFor="postal_code" className="form-label">
                    Código postal
                  </label>
                  <input
                    id="postal_code"
                    type="text"
                    className="form-input"
                    placeholder="28001, 08001, etc."
                    {...register('postal_code')}
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="phone" className="form-label">
                    Teléfono
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    className={`form-input ${errors.phone ? 'border-danger-500' : ''}`}
                    placeholder="+34 123 456 789"
                    {...register('phone', {
                      pattern: /^[\+]?[0-9\s\-\(\)]+$/,
                    })}
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-danger-600">
                      Formato de teléfono inválido
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" className="form-label">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    className={`form-input ${errors.email ? 'border-danger-500' : ''}`}
                    placeholder="contacto@establecimiento.com"
                    {...register('email', {
                      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    })}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-danger-600">
                      Formato de email inválido
                    </p>
                  )}
                </div>
              </div>

              {/* Website */}
              <div>
                <label htmlFor="website" className="form-label">
                  Sitio web
                </label>
                <input
                  id="website"
                  type="url"
                  className={`form-input ${errors.website ? 'border-danger-500' : ''}`}
                  placeholder="https://www.establecimiento.com"
                  {...register('website', {
                    pattern: /^https?:\/\/.+/,
                  })}
                />
                {errors.website && (
                  <p className="mt-1 text-sm text-danger-600">
                    Debe ser una URL válida (comenzar con http:// o https://)
                  </p>
                )}
              </div>

              {/* Active status */}
              <div className="flex items-center">
                <input
                  id="is_active"
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  {...register('is_active')}
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                  Establecimiento activo
                </label>
              </div>

              {/* Show Schedule Colors */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1 pr-4">
                    <div className="flex items-center gap-2 mb-1">
                      <FiDroplet className="text-purple-600" />
                      <span className="font-medium text-gray-900">
                        Mostrar colores en cuadrantes
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Cuando está activado, se mostrará una vista adicional de colores en los cuadrantes
                      basada en los grupos de habitaciones asignados a cada empleado. Las horas siempre
                      estarán disponibles.
                    </p>
                  </div>
                  <div className="flex items-center">
                    <button
                      type="button"
                      onClick={() => setValue('show_schedule_colors', !showScheduleColors)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                        showScheduleColors ? 'bg-purple-600' : 'bg-gray-200'
                      }`}
                      role="switch"
                      aria-checked={showScheduleColors}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          showScheduleColors ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

        </form>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          onClick={onClose}
          className="btn btn-secondary"
          disabled={isSubmitting}
        >
          Cancelar
        </button>
        <button
          type="submit"
          form="establishment-form"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <div className="flex items-center space-x-2">
              <LoadingSpinner size="sm" color="white" />
              <span>Guardando...</span>
            </div>
          ) : (
            isEditing ? 'Actualizar' : 'Crear'
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default EstablishmentForm;